import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import App from './App';
import { TenantProvider, useTenant } from './context/TenantContext';
import './styles/index.css';

// Create theme based on tenant colors (using CSS variables as fallback)
const createDynamicTheme = (primaryColor, secondaryColor) => {
  return createTheme({
    palette: {
      primary: {
        main: primaryColor || '#06AB8B',
        light: primaryColor ? `${primaryColor}99` : '#3bc4a6',
        dark: secondaryColor || '#049572',
        contrastText: '#ffffff',
      },
      secondary: {
        main: secondaryColor || '#049572',
        contrastText: '#ffffff',
      },
      background: {
        default: '#f5f7fa',
        paper: '#ffffff',
      },
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    },
    shape: {
      borderRadius: 8,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
          },
        },
      },
    },
  });
};

// Default theme
const defaultTheme = createDynamicTheme('#06AB8B', '#049572');

// Themed App wrapper that updates theme based on tenant
const ThemedApp = () => {
  const { tenant, loading } = useTenant();

  // Create theme based on tenant colors
  const theme = React.useMemo(() => {
    if (tenant && tenant.primaryColor) {
      return createDynamicTheme(tenant.primaryColor, tenant.secondaryColor);
    }
    return defaultTheme;
  }, [tenant]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <App />
    </ThemeProvider>
  );
};

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <BrowserRouter>
      <TenantProvider>
        <ThemedApp />
      </TenantProvider>
    </BrowserRouter>
  </React.StrictMode>
);
